import { Component, ElementRef, OnChanges, OnDestroy, AfterViewInit, ViewChild, input, output, signal, effect, untracked, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToBanglaNumberPipe } from '../../../../shared/pipes/toBanglaNumber.pipe';

@Component({
  selector: 'app-match-gauge',
  standalone: true,
  imports: [CommonModule, ToBanglaNumberPipe],
  template: `
    <div class="w-[75px] md:w-44 flex flex-col items-center md:pt-3 pb-0 md:pb-4 px-4 box-border font-sans">
      <div class="relative w-[75px] h-[50px] md:w-[164px] md:h-[99px]">
        <svg viewBox="0 0 260 150" class="w-full h-full overflow-visible">
          <defs>
            <linearGradient [id]="gradientId" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="#e9e8f5"/>
              <stop offset="45%" stop-color="#8b6ff0"/>
              <stop offset="100%" stop-color="#5b21e8"/>
            </linearGradient>
            @if(!isDisabled()){
              <filter [id]="filterId" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#5b21e8" flood-opacity="0.35"/>
              </filter>
            }
          </defs>
          <path d="M 20 130 A 110 110 0 0 1 240 130"
                fill="none"
                stroke="#ececf1"
                stroke-width="30"
                stroke-linecap="round"/>

          <path #progressArc
                d="M 20 130 A 110 110 0 0 1 240 130"
                fill="none"
                [attr.stroke]="'url(#' + gradientId + ')'"
                stroke-width="30"
                stroke-linecap="round"/>

          <!-- Knob: drawn last so it always paints in front of the arc -->
          <g #knob [attr.filter]="isDisabled() ? null : 'url(#' + filterId + ')'">
            <rect #knobRect
              x="-24" y="0" width="46" height="14" rx="7" ry="7"
              fill="#ffffff" [attr.stroke]="isDisabled() ? '#D0D5DD' : '#6d3ff0'" stroke-width="2"
            />
          </g>
        </svg>

        <div class="absolute left-0 right-0 bottom-1 md:bottom-2 text-center">
          @if(isMatchingDataReady() && !isDisabled()){
            <div class="text-[10px] md:text-[24px] font-semibold text-[#101828] tracking-tight leading-none">
              {{ currentLang() === 'bn' ? (displayPercent() | toBanglaNumber) : displayPercent() }}%
            </div>
            <div class="mt-0.5 text-[8px] md:text-sm font-medium text-[#475467]">
              {{ label() }}
            </div>
          }@else {
            <div class="whitespace-pre-line text-[8px] leading-tight md:text-sm md:leading-tight font-medium text-[#475467]">
              {{ fallbackLabel() }}
            </div>
          }
        </div>
      </div>
    </div>
  `
})
export class MatchGaugeComponent implements AfterViewInit, OnChanges, OnDestroy {
  private static instanceCounter = 0;
  protected readonly instanceId = `gauge_${++MatchGaugeComponent.instanceCounter}`;
  protected readonly gradientId = `arcGradient_${this.instanceId}`;
  protected readonly filterId = `knobShadow_${this.instanceId}`;

  percent = input(0);
  label = input('Matched');
  // Transloco exposes the active language as `string` in templates. Keep this
  // input compatible with that contract; this component only treats `bn`
  // specially and uses English for every other value.
  currentLang = input<string>('en');
  fallbackLabel = input('Ready\nto Match');
  isDisabled = input(false);
  isMatchingDataReady = input(false);
  hasIntroPlayed = input(false);
  introCompleted = output<void>();
  displayPercent = signal(0);

  @ViewChild('progressArc', { static: true }) progressArcRef!: ElementRef<SVGPathElement>;
  @ViewChild('knob', { static: true }) knobRef!: ElementRef<SVGGElement>;
  @ViewChild('knobRect', { static: true }) knobRectRef!: ElementRef<SVGRectElement>;

  private viewReady = false;
  private rafId: number | null = null;
  private resolveCurrentSegment: (() => void) | null = null;
  private currentPercent = 0;
  protected introPlayed = signal(false);
  private isIntroRunning = false;
  private introCancelled = false;
  private intersectionObserver: IntersectionObserver | null = null;
  private hostEl = inject(ElementRef);

  constructor() {
    effect(() => {
      const p = this.percent();
      const ready = this.isMatchingDataReady();
      const disabled = this.isDisabled();
      const introAlreadyDone = this.hasIntroPlayed() || this.introPlayed();
      console.log(`[MatchGauge ${this.instanceId}] Signal change detected -> percent: ${p}, ready: ${ready}, disabled: ${disabled}, introDone: ${introAlreadyDone}`);

      if (this.viewReady) {
        untracked(() => {
          if (ready) {
            // If the first-time intro sweep was running when API data arrived, cancel it
            if (this.isIntroRunning) {
              this.introCancelled = true;
              this.isIntroRunning = false;
            }
            this.introPlayed.set(true);
            this.introCompleted.emit();
            this.animateTo(disabled ? 0 : p);
          } else if (introAlreadyDone) {
            this.animateTo(0);
          }
        });
      }
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.setupIntersectionObserver();

    // If API data is already loaded, or if intro already played:
    // DO NOT go 0 to 100! Settle / animate directly to the target percent.
    if (this.isMatchingDataReady() || this.hasIntroPlayed()) {
      this.introPlayed.set(true);
      const target = this.isDisabled() || !this.isMatchingDataReady() ? 0 : this.percent();
      console.log(`[MatchGauge ${this.instanceId}] Initialized with ready data or previous intro. Settling directly to ${target}%`);
      this.animateTo(target);
    } else {
      // First time on load (no API data yet): play intro sweep 0 -> 100 -> 0, then reset to 0
      this.playIntro();
    }
  }

  private setupIntersectionObserver(): void {
    if (typeof IntersectionObserver === 'undefined') return;
    this.intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          console.log(`[MatchGauge ${this.instanceId}] Entered viewport / became visible. introPlayed: ${this.introPlayed()}`);
          if (this.introPlayed() || this.hasIntroPlayed() || this.isMatchingDataReady()) {
            this.animateTo(this.isDisabled() || !this.isMatchingDataReady() ? 0 : this.percent());
          }
        }
      }
    }, { threshold: 0.1 });

    this.intersectionObserver.observe(this.hostEl.nativeElement);
  }

  ngOnChanges(): void {
    if (this.viewReady && (this.introPlayed() || this.hasIntroPlayed() || this.isMatchingDataReady())) {
      this.animateTo(this.isDisabled() || !this.isMatchingDataReady() ? 0 : this.percent());
    }
  }

  ngOnDestroy(): void {
    this.introCancelled = true;
    this.isIntroRunning = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.resolveCurrentSegment !== null) {
      const prevResolve = this.resolveCurrentSegment;
      this.resolveCurrentSegment = null;
      prevResolve();
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  /** On first load (without API data): sweep 0 -> 100 -> 0, then settle into the fallback/target percent. */
  private async playIntro(): Promise<void> {
    if (this.isMatchingDataReady() || this.hasIntroPlayed()) {
      this.introPlayed.set(true);
      const finalTarget = this.isDisabled() || !this.isMatchingDataReady() ? 0 : this.percent();
      this.animateTo(finalTarget);
      return;
    }

    this.isIntroRunning = true;
    this.introCancelled = false;
    console.log(`[MatchGauge ${this.instanceId}] Starting first-time intro sweep 0 -> 100 -> 0...`);

    try {
      await this.animateSegment(0, 100, 1600);
      if (this.introCancelled || this.isMatchingDataReady()) {
        return;
      }
      await this.animateSegment(100, 0, 1600);
      if (this.introCancelled || this.isMatchingDataReady()) {
        return;
      }
    } finally {
      this.isIntroRunning = false;
    }

    this.introPlayed.set(true);
    this.introCompleted.emit();
    const finalTarget = this.isDisabled() || !this.isMatchingDataReady() ? 0 : this.percent();
    console.log(`[MatchGauge ${this.instanceId}] Intro sweep complete. Settling to target: ${finalTarget}% (ready: ${this.isMatchingDataReady()})`);
    this.animateTo(finalTarget);
  }

  /** Drives the arc, knob, and number off a single rAF loop so they stay perfectly in sync. */
  private animateTo(target: number): void {
    const clampedTarget = Math.max(0, Math.min(100, target));
    if (Math.abs(this.currentPercent - clampedTarget) < 0.1) {
      this.currentPercent = clampedTarget;
      this.displayPercent.set(this.isDisabled() ? 0 : Math.round(clampedTarget));
      const arc = this.progressArcRef.nativeElement;
      this.paintAt(arc, this.getArcLength(arc), clampedTarget);
      return;
    }
    console.log(`[MatchGauge ${this.instanceId}] animateTo: from ${this.currentPercent}% to ${clampedTarget}%`);
    this.animateSegment(this.currentPercent, clampedTarget, 1200);
  }

  private getArcLength(arc: SVGPathElement): number {
    try {
      const len = arc.getTotalLength();
      if (len && len > 0) {
        return len;
      }
    } catch {
      // Fallback below
    }
    // Semicircle M 20 130 A 110 110 0 0 1 240 130 has R = 110, angle = PI -> PI * 110 ≈ 345.575
    return Math.PI * 110;
  }

  /** Animates from `from` to `to` over `duration` ms, resolving when the segment finishes. */
  private animateSegment(from: number, to: number, duration: number): Promise<void> {
    return new Promise((resolve) => {
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      if (this.resolveCurrentSegment !== null) {
        const prevResolve = this.resolveCurrentSegment;
        this.resolveCurrentSegment = null;
        prevResolve();
      }
      this.resolveCurrentSegment = resolve;

      const clampedTarget = Math.max(0, Math.min(100, to));
      const startTime = performance.now();

      const arc = this.progressArcRef.nativeElement;
      const length = this.getArcLength(arc);
      arc.style.strokeDasharray = `${length}`;

      const step = (now: number): void => {
        const t = Math.min(1, (now - startTime) / duration);
        const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
        const value = from + (clampedTarget - from) * eased;

        this.paintAt(arc, length, value);
        this.displayPercent.set(this.isDisabled() ? 0 : Math.round(value));

        if (t < 1) {
          this.rafId = requestAnimationFrame(step);
        } else {
          this.currentPercent = clampedTarget;
          this.rafId = null;
          this.resolveCurrentSegment = null;
          resolve();
        }
      };

      this.rafId = requestAnimationFrame(step);
    });
  }

  /** Positions the arc fill and the knob (with rotation) at a given percent along the path. */
  private paintAt(arc: SVGPathElement, length: number, percent: number): void {
    const drawLength = (percent / 100) * length;
    arc.style.strokeDashoffset = `${length - drawLength}`;

    const tipLength = Math.min(length, drawLength);
    const behindLength = Math.max(0, tipLength - 1);

    let tip = { x: 20, y: 130 };
    let behind = { x: 20, y: 130 };

    try {
      const t = arc.getPointAtLength(tipLength);
      const b = arc.getPointAtLength(behindLength);
      if (t.x !== 0 || t.y !== 0 || tipLength === 0) {
        tip = t;
        behind = b;
      } else {
        tip = this.calculatePointOnArc(percent);
        behind = this.calculatePointOnArc(Math.max(0, percent - 0.5));
      }
    } catch {
      tip = this.calculatePointOnArc(percent);
      behind = this.calculatePointOnArc(Math.max(0, percent - 0.5));
    }

    const angleRad = Math.atan2(tip.y - behind.y, tip.x - behind.x);
    const angleDeg = (angleRad * 180) / Math.PI;

    this.knobRef.nativeElement.setAttribute(
      'transform',
      `translate(${tip.x}, ${tip.y}) rotate(${angleDeg})`
    );

    const isMoving = percent > 0;
    this.knobRectRef.nativeElement.setAttribute('y', isMoving ? '-14' : '0');
    this.knobRectRef.nativeElement.setAttribute('transform', isMoving ? 'rotate(95)' : 'rotate(0)');
  }

  private calculatePointOnArc(percent: number): { x: number; y: number } {
    // Semicircle with center (130, 130) and radius 110:
    // At 0%: angle = 0 rad -> x = 20, y = 130
    // At 50%: angle = PI/2 rad -> x = 130, y = 20
    // At 100%: angle = PI rad -> x = 240, y = 130
    const angleRad = (percent * 1.8 * Math.PI) / 180;
    const x = +(130 - 110 * Math.cos(angleRad)).toFixed(2);
    const y = +(130 - 110 * Math.sin(angleRad)).toFixed(2);
    return { x, y };
  }
}
